import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api, setUnauthorizedHandler } from "../lib/apiClient";

export interface User {
  id: string;
  username: string;
  full_name: string;
  roles: string[];
  is_department_head?: boolean;
  must_change_password?: boolean;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface MeResponse {
  user: User;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  login: (username: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  // VF-002: щойно login/logout встановили стан, відкладений результат bootstrap
  // `GET /me` НЕ повинен його затирати (гонка повільного /me проти швидкого login).
  const bootstrapActiveRef = useRef(true);

  useEffect(() => {
    bootstrapActiveRef.current = true;
    api
      .get<MeResponse>("/api/auth/me")
      .then((data) => {
        if (!bootstrapActiveRef.current) return;
        setUser(data.user);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!bootstrapActiveRef.current) return;
        setUser(null);
        setStatus("unauthenticated");
      });
    return () => {
      bootstrapActiveRef.current = false;
    };
  }, []);

  // Реактивний sign-out: коли апі-клієнт вичерпав тихий refresh на 401 (мертвий
  // refresh-токен), скидаємо стан ТУТ → ProtectedRoute веде на /login БЕЗ ручного
  // перезавантаження сторінки (симптом «розлогінило, а логін-екран не зʼявився»).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      bootstrapActiveRef.current = false;
      setUser(null);
      setStatus("unauthenticated");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(
    async (username: string, password: string, totpCode?: string) => {
      const data = await api.post<MeResponse>("/api/auth/login", {
        username,
        password,
        totp_code: totpCode ?? null,
      });
      bootstrapActiveRef.current = false; // мутація має пріоритет над відкладеним /me
      setUser(data.user);
      setStatus("authenticated");
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      bootstrapActiveRef.current = false;
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  // Пере-читання /me після мутацій власного акаунта (зміна пароля знімає
  // must_change_password на бекенді — стан у SPA має це відбити одразу).
  const refreshUser = useCallback(async () => {
    const data = await api.get<MeResponse>("/api/auth/me");
    bootstrapActiveRef.current = false;
    setUser(data.user);
    setStatus("authenticated");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, refreshUser }),
    [status, user, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth має використовуватися всередині <AuthProvider>");
  }
  return context;
}
